import React, { Component } from "react";
import PropTypes from "prop-types";
import IdealStudio from "../../../core/editor/main";
import Alert from "../common/alert/Alert";
import {
  isContainedView,
  isSlide,
  isBox,
  isSortableBox,
  isView,
  isSortableContainer,
} from "../../../common/utils";
import {
  ID_PREFIX_BOX,
  ID_PREFIX_SORTABLE_CONTAINER,
  ID_PREFIX_RICH_MARK,
} from "../../../common/constants";
import { ADD_BOX } from "../../../common/actions";
import {
  randomPositionGenerator,
  retrieveImageFromClipboardAsBase64,
  getCKEDITORAdaptedContent,
  isURL,
  copyText,
  collectScene,
  classifyAssets,
  rekeyScene,
  needsAssetCopy,
  collectBoxAssets,
  applyUrlMap,
} from "./clipboard.utils";
import i18n from "i18next";
import { instanceExists, scrollElement, findBox, createBox } from "../../../common/common_tools";
import {
  isClmCourse,
  isClmAllowedPlugin,
  findDisallowedClmPluginInScene,
} from "../../../common/clm";
import ScenePasteModal from "./ScenePasteModal";
import { isContentEditableFocus } from "../../utils/undoRedoShortcut";
import { getOrganizationId, getToken } from "../../../common/environment_config";
import { apiPost } from "../../../common/http_service";

// Frame-synced media plugins (video/audio). Mirrors the layer-type detection
// in IdeaTimeline/utils/buildLayers.js.
function isMediaPluginId(pluginId) {
  if (!pluginId) return false;
  const p = pluginId.toLowerCase();
  return p.includes("video") || p.includes("player") || p.includes("audio") || p.includes("sound");
}

/**
 * Component for managing the clipboard
 */
export default class Clipboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      alert: null,
      counter: 0,
      scenePasteModal: null, // { payload, crossTraining, busy }
    };
    this.copyButtonListener = this.copyButtonListener.bind(this);
    this.copyListener = this.copyListener.bind(this);
    this.pasteListener = this.pasteListener.bind(this);
    this.cutListener = this.cutListener.bind(this);
    this.pasteBox = this.pasteBox.bind(this);
    this.copyData = this.copyData.bind(this);
    this.duplicateBox = this.duplicateBox.bind(this);
    this.cropToNewBox = this.cropToNewBox.bind(this);
    this.duplicateListener = this.duplicateListener.bind(this);
    this.currentPage = this.currentPage.bind(this);
    this.getIndex = this.getIndex.bind(this);
    this.copyScene = this.copyScene.bind(this);
    this.beginScenePaste = this.beginScenePaste.bind(this);
    this.confirmScenePaste = this.confirmScenePaste.bind(this);
    this._scenePasteIdCounter = 0;
  }

  /**
   * Copies the current scene to the system clipboard as a tagged JSON payload.
   * Called via the dock button (E6) or Ctrl+C when nothing is selected (E6).
   */
  copyScene() {
    const sceneId = this.props.navItemSelected;
    if (!sceneId || sceneId === 0) return;

    const state = {
      navItemsById: this.props.navItems || {},
      boxesById: this.props.boxes || {},
      pluginToolbarsById: this.props.toolbars || {},
      marksById: this.props.marks || {},
      viewToolbarsById: this.props.viewToolbars || {},
      exercises: this.props.exercises || {},
      animationsById: this.props.animations || {},
      soundList: this.props.soundList || {},
      containedViewsById: this.props.containedViews || {},
    };

    let scene;
    try {
      scene = collectScene(sceneId, state);
    } catch (e) {
      console.error("copyScene: collectScene failed", e);
      return;
    }

    const { assets, assetCounts } = classifyAssets(scene);

    const payload = {
      __aideaType: "scene",
      version: 1,
      source: {
        trainingId: window.idealStudioTrainingId,
        organizationId: getOrganizationId(),
        projectId: window.idealStudioProjectId || null,
      },
      scene,
      assets,
      assetCounts,
    };

    copyText(JSON.stringify(payload));

    this.setState({
      alert: (
        <Alert
          className="pageModal"
          key="scene-copied-alert"
          show
          hasHeader={false}
          backdrop={false}
          acceptButtonText="OK"
          onClose={() => this.setState({ alert: null })}
        >
          {i18n.t("scenePaste.copied", "Scene copied — paste it into any training.")}
        </Alert>
      ),
    });
  }

  /**
   * Opens the ScenePasteModal after detecting a scene payload on the clipboard.
   */
  beginScenePaste(payload) {
    const crossTraining =
      payload.source && payload.source.trainingId !== window.idealStudioTrainingId;

    this.setState({
      scenePasteModal: { payload, crossTraining, busy: false },
    });
  }

  /**
   * Called when the user clicks "Add scene" in ScenePasteModal.
   */
  async confirmScenePaste() {
    const { scenePasteModal } = this.state;
    if (!scenePasteModal) return;
    const { payload, crossTraining } = scenePasteModal;

    this.setState({ scenePasteModal: { ...scenePasteModal, busy: true } });

    let urlMap = {};
    try {
      if (crossTraining) {
        const base = window.ideaLibraryRootUrl || "";
        const url = base + "api/studio/scene/copy-assets";
        const body = {
          sourceTrainingId: payload.source.trainingId,
          sourceOrganizationId: payload.source.organizationId,
          sourceProjectId: payload.source.projectId || undefined,
          destTrainingId: window.idealStudioTrainingId,
          assets: payload.assets,
        };
        const resp = await apiPost(url, body, {
          headers: { Authorization: "Bearer " + getToken() },
        });
        if (!resp.ok) {
          const text = await resp.text();
          throw new Error("copy-assets failed: " + resp.status + " " + text);
        }
        const json = await resp.json();
        urlMap = json.urlMap || {};
      }

      // id factory — unique across this call. Preserve the original id's prefix
      // (pa-/bo-/cv-/bs-/…) so the pasted scene stays a page (isPage), contained
      // views stay contained views, sortable boxes stay sortable, etc.
      let _counter = 0;
      const prefixOf = (id) => {
        const i = typeof id === "string" ? id.indexOf("-") : -1;
        return i > -1 ? id.slice(0, i + 1) : ID_PREFIX_BOX;
      };
      const idFactory = (oldId) => prefixOf(oldId) + Date.now() + "_cs" + _counter++;

      const blockedPlugin = isClmCourse(this.props.globalConfig)
        ? findDisallowedClmPluginInScene(payload.scene)
        : null;
      if (blockedPlugin) {
        throw new Error(i18n.t("messages.clm_plugin_blocked") + " (" + blockedPlugin + ")");
      }

      const rekeyed = rekeyScene(payload.scene, urlMap, idFactory);

      // Insert scene after current
      if (this.props.onScenePasted) {
        this.props.onScenePasted(rekeyed);
      }

      // Auto-save
      if (this.props.onSave) {
        this.props.onSave();
      }

      // Select the new scene
      if (this.props.onNavItemSelected && rekeyed.newSceneId) {
        this.props.onNavItemSelected(rekeyed.newSceneId);
      }

      this.setState({
        scenePasteModal: null,
        alert: (
          <Alert
            className="pageModal"
            key="scene-pasted-alert"
            show
            hasHeader={false}
            backdrop={false}
            acceptButtonText="OK"
            onClose={() => this.setState({ alert: null })}
          >
            {i18n.t("scenePaste.done", "Done — scene added.")}
          </Alert>
        ),
      });
    } catch (err) {
      console.error("confirmScenePaste error:", err);
      this.setState({
        scenePasteModal: null,
        alert: (
          <Alert
            className="pageModal"
            key="scene-paste-error"
            show
            hasHeader
            title={i18n.t("messages.alert", "Alert")}
            closeButton
            backdrop={false}
            onClose={() => this.setState({ alert: null })}
          >
            {i18n.t("scenePaste.error", "Failed to paste scene: ") + err.message}
          </Alert>
        ),
      });
    }
  }

  /**
   * Extracts necessary information for clipboard/duplicating
   */
  copyData() {
    let copyData = [];
    let boxselectedData = this.currentPage();
    let box = this.props.boxes[this.props.boxSelected];
    if (!box) {
      return null;
    }
    let toolbar = this.props.toolbars[this.props.boxSelected];
    let itemSelected = this.currentPage();
    let score;
    if (itemSelected.id) {
      let exercisePage = this.props.exercises[itemSelected.id];
      score = exercisePage.exercises[this.props.boxSelected];
    }
    let childBoxes = {};
    let childToolbars = {};
    if (box.sortableContainers) {
      for (let sc in box.sortableContainers) {
        for (let b in box.sortableContainers[sc].children) {
          let bid = box.sortableContainers[sc].children[b];
          childBoxes[bid] = this.props.boxes[bid];
          childToolbars[bid] = this.props.toolbars[bid];
        }
      }
    }
    const source = {
      trainingId: window.idealStudioTrainingId,
      organizationId: getOrganizationId(),
      projectId: window.idealStudioProjectId || null,
    };
    let marks = {};
    for (let id in this.props.marks) {
      let mark = this.props.marks[id];
      if (mark.origin === this.props.boxSelected) {
        marks[id] = mark;
      }
      if (Object.keys(childBoxes).indexOf(mark.origin) > -1) {
        marks[id] = mark;
      }
    }

    if (this.props.boxSelectedAll.length > 1) {
      boxselectedData.boxes.map((boxId) => {
        let box = this.props.boxes[boxId];
        let toolbar = this.props.toolbars[boxId];
        let itemSelected = this.currentPage();
        let score;
        if (itemSelected.id) {
          let exercisePage = this.props.exercises[itemSelected.id];
          score = exercisePage.exercises[boxId];
        }
        let childBoxes = {};
        let childToolbars = {};
        if (box.sortableContainers) {
          for (let sc in box.sortableContainers) {
            for (let b in box.sortableContainers[sc].children) {
              let bid = box.sortableContainers[sc].children[b];
              childBoxes[bid] = this.props.boxes[bid];
              childToolbars[bid] = this.props.toolbars[bid];
            }
          }
        }
        let marks = {};
        for (let id in this.props.marks) {
          let mark = this.props.marks[id];
          if (mark.origin === boxId) {
            marks[id] = mark;
          }
          if (Object.keys(childBoxes).indexOf(mark.origin) > -1) {
            marks[id] = mark;
          }
        }
        copyData.push({ box, toolbar, marks, childBoxes, childToolbars, score, source });
      });
      return copyData;
    }
    return { box, toolbar, marks, childBoxes, childToolbars, score, source };
  }

  /**
   * Cross-training element paste: copies the element's assets into the current
   * training (same endpoint as scene paste) and returns the data with rewritten
   * URLs. On failure warns and returns the data with the original URLs.
   */
  async copyBoxAssets(data) {
    const assets = collectBoxAssets(data);
    if (!assets.length) return applyUrlMap(data, {});
    try {
      const resp = await apiPost(
        (window.ideaLibraryRootUrl || "") + "api/studio/scene/copy-assets",
        {
          sourceTrainingId: data.source.trainingId,
          sourceOrganizationId: data.source.organizationId,
          sourceProjectId: data.source.projectId || undefined,
          destTrainingId: window.idealStudioTrainingId,
          assets,
        },
        { headers: { Authorization: "Bearer " + getToken() } }
      );
      if (!resp.ok) {
        throw new Error("copy-assets failed: " + resp.status + " " + (await resp.text()));
      }
      const json = await resp.json();
      return applyUrlMap(data, json.urlMap || {});
    } catch (err) {
      console.error("copyBoxAssets error:", err);
      this.setState({
        alert: (
          <Alert
            className="pageModal"
            key="box-paste-assets-warning"
            show
            hasHeader
            title={i18n.t("messages.alert", "Alert")}
            closeButton
            backdrop={false}
            onClose={() => this.setState({ alert: null })}
          >
            {i18n.t(
              "boxPaste.assetsWarning",
              "Could not copy the element's media. It still points to the original training and may not display for viewers: "
            ) + err.message}
          </Alert>
        ),
      });
      return applyUrlMap(data, {});
    }
  }
  /**
   * Copy action listener
   * @param event
   */

  copyListener(event) {
    let boxselectedCurrentPage = this.currentPage();
    let activeElement = document.activeElement;
    // If the user has an actual (non-collapsed) text selection, let the browser
    // perform the native text copy. Otherwise selecting text inside a plugin box
    // (e.g. Matching/MultipleChoice) would clobber the clipboard with the box's
    // serialized JSON instead of the highlighted text.
    if (this.hasTextSelection()) {
      return false;
    }
    if (event.clipboardData) {
      // When nothing is selected and focus is not in a text field, copy the scene
      if (this.props.boxSelected === -1 && !this.containsCKEDitorText(activeElement)) {
        event.preventDefault();
        this.copyScene();
        return true;
      }
      if (this.props.boxSelected !== -1 && !isSortableBox(this.props.boxSelected)) {
        if (!this.containsCKEDitorText(activeElement)) {
          const data = this.copyData();
          if (!data) {
            return false;
          }
          event.preventDefault();
          event.clipboardData.setData("text/plain", JSON.stringify(data));
          document.activeElement.blur();
          return true;
        }
      }
      return true;
    }
    document.activeElement.blur();

    if (this.props.boxSelectedAll.length > 1) {
      boxselectedCurrentPage.map((e) => {
        let activeElement = document.activeElement;
        if (event.clipboardData) {
          if (e !== -1 && !isSortableBox(e)) {
            if (!this.containsCKEDitorText(activeElement)) {
              event.preventDefault();
              event.clipboardData.setData("text/plain", JSON.stringify(this.copyData()));
              document.activeElement.blur();
              return true;
            }
          }
          return true;
        }
        document.activeElement.blur();
        return false;
      });
    }

    return false;
  }

  /**
   * Copy Button
   * @param event
   * @returns {boolean}
   */
  copyButtonListener(event) {
    let activeElement = document.activeElement;
    if (this.props.boxSelected !== -1 && !isSortableBox(this.props.boxSelected)) {
      if (
        !this.containsCKEDitorText(activeElement) ||
        (this.props.boxes[this.props.boxSelected] &&
          !this.props.boxes[this.props.boxSelected].showTextEditor)
      ) {
        const data = this.copyData();
        if (!data) {
          return false;
        }
        return copyText(data);
      }
      try {
        document.execCommand("copy");
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }
  /**
   * Cut action listener
   */
  cutListener(event) {
    let fromPlugin = this.copyListener(event);
    if (fromPlugin && !this.props.openTextEditor) {
      let box = this.props.boxes[this.props.boxSelected];
      this.props.onBoxDeleted(box.id, box.parent, box.container, this.currentPage());
    }
    document.activeElement.blur();
  }
  /**
   * Calculates current page (nav or cv)
   */
  currentPage() {
    return isContainedView(this.props.containedViewSelected)
      ? this.props.containedViews[this.props.containedViewSelected]
      : this.props.navItemSelected !== 0
        ? this.props.navItems[this.props.navItemSelected]
        : null;
  }

  /**
   * Duplicates box
   */
  duplicateBox() {
    let data = this.copyData();
    let containerId = ID_PREFIX_SORTABLE_CONTAINER + Date.now();
    let id = ID_PREFIX_BOX + Date.now();
    let page = this.currentPage();

    let isTargetSlide = isSlide(page.type);
    let parent = isTargetSlide ? page.id : page.boxes[0];

    let container = isTargetSlide ? 0 : containerId;
    let newInd;
    if (
      this.props.boxSelected &&
      this.props.boxes[this.props.boxSelected] &&
      isBox(this.props.boxSelected)
    ) {
      parent = this.props.boxes[this.props.boxSelected].parent;
      container = this.props.boxes[this.props.boxSelected].container;
      // isTargetSlide = container === 0;
      newInd = this.getIndex(parent, container);
    }
    let ids = { id, parent, container, page: page ? page.id : 0 };
    this.pasteBox(data, ids, isTargetSlide, newInd);
    document.activeElement.blur();
  }

  /**
   * Crop → new object: clone the currently-selected image box using `croppedUrl`
   * (the cropped PNG, uploaded to the training as a new file) as its image source,
   * delete the original box, and return the new box id. Mirrors duplicateBox()
   * but swaps state.url and removes the source so the crop appears as a fresh,
   * independent object — the source image's library asset is never touched.
   * Geometry (position/size) is applied by the caller via resizeBox on the
   * returned id.
   */
  cropToNewBox(croppedUrl) {
    let data = this.copyData();
    // Single image box only (multi-selection returns an array).
    if (!data || Array.isArray(data) || !data.box || !data.toolbar) {
      return null;
    }
    // Deep-clone so we never mutate the store's box/toolbar refs.
    data = JSON.parse(JSON.stringify(data));
    // Snapshot the pre-crop image + box geometry so ToolsPanel can offer a
    // "restore to original" action (the crop replaces the box outright, so
    // without this the uncropped image is otherwise unrecoverable). Preserve an
    // existing snapshot when re-cropping an already-cropped box, so restore
    // always returns to the very first original rather than the last crop.
    const prevSnapshot = data.toolbar.structure && data.toolbar.structure.__cropOriginal;
    const origStructure = data.toolbar.structure || {};
    const origPos = data.box.position || {};
    const cropOriginal = prevSnapshot || {
      url: (data.toolbar.state || {}).url,
      width: origStructure.width,
      widthUnit: origStructure.widthUnit,
      height: origStructure.height,
      heightUnit: origStructure.heightUnit,
      x: origPos.x,
      y: origPos.y,
    };
    data.toolbar.state = { ...(data.toolbar.state || {}), url: croppedUrl };
    data.toolbar.structure = { ...origStructure, __cropOriginal: cropOriginal };

    let originalId = this.props.boxSelected;
    let originalBox = this.props.boxes[originalId];

    let id = ID_PREFIX_BOX + Date.now();
    let page = this.currentPage();
    let isTargetSlide = isSlide(page.type);
    let parent = isTargetSlide ? page.id : page.boxes[0];
    let container = 0;
    let newInd;
    if (originalBox && isBox(originalId)) {
      parent = originalBox.parent;
      container = originalBox.container;
      newInd = this.getIndex(parent, container);
    }
    let ids = { id, parent, container, page: page ? page.id : 0 };
    this.pasteBox(data, ids, isTargetSlide, newInd);

    // Remove the source box from the scene (its underlying library asset stays
    // intact — re-adding from the library brings back the original image).
    if (originalBox) {
      this.props.onBoxDeleted(
        originalId,
        originalBox.parent,
        originalBox.container,
        page ? page.id : 0
      );
    }
    return id;
  }

  /**
   * Duplicate action listener
   */
  duplicateListener(event) {
    let key = event.keyCode ? event.keyCode : event.which;
    // AltGr on Windows reports Ctrl+Alt, so AltGr+E (€) typed into a text
    // field must not duplicate the selected box.
    const target = document.activeElement;
    const typing =
      !!target &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) || isContentEditableFocus(target));
    if (key === 69 && event.ctrlKey && event.altKey && !typing && isBox(this.props.boxSelected)) {
      event.preventDefault();
      event.stopPropagation();
      this.duplicateBox();
    } else if (key === 86 && event.ctrlKey && event.shiftKey) {
      this.pasteListener(event, true);
    }
    return true;
  }

  /**
   * Pastes box
   */
  pasteBox(data, ids, isTargetSlide, index) {
    if (needsAssetCopy(data, window.idealStudioTrainingId)) {
      this.copyBoxAssets(data).then((d) => this.pasteBox(d, ids, isTargetSlide, index));
      return;
    }
    let pluginName = data.toolbar.pluginId;
    let plug = IdealStudio.Plugins.get(pluginName);
    if (!plug) {
      return;
    }
    let config = plug.getConfig();
    let limitToOneInstance = config.limitToOneInstance;
    let alertMsg = (msg) => {
      return (
        <Alert
          className="pageModal"
          key="alert"
          show
          hasHeader
          backdrop={false}
          title={
            <span>
              <i className="material-icons alert-warning">warning</i>
              {i18n.t("messages.alert")}
            </span>
          }
          closeButton
          onClose={() => {
            this.setState({ alert: null });
          }}
        >
          <span> {msg} </span>
        </Alert>
      );
    };
    // Forbid plugins inside plugins inside plugins (only 1 level allowed)
    if (isBox(ids.parent) && (!data.childBoxes || Object.keys(data.childBoxes).length > 0)) {
      this.setState({ alert: alertMsg(i18n.t("messages.depth_limit")) });
      return;
    }
    if (limitToOneInstance && instanceExists(data.toolbar.pluginId)) {
      this.setState({ alert: alertMsg(i18n.t("messages.instance_limit")) });
      return;
    }
    // CLM courses: only allowlisted plugins may enter the scene (paste,
    // duplicate and keyboard duplicate all route through here).
    if (isClmCourse(this.props.globalConfig) && !isClmAllowedPlugin(pluginName)) {
      this.setState({ alert: alertMsg(i18n.t("messages.clm_plugin_blocked")) });
      return;
    }

    let transformedBox = this.transformBox(data.box, ids, isTargetSlide, data.box.resizable);
    let transformedToolbar = this.transformToolbar(
      data.toolbar,
      ids,
      isTargetSlide,
      data.box.resizable
    );
    // Cross-scene/training paste: a media clip's timeline frames are relative to
    // its origin scene. Carried verbatim they leave the frame-synced player
    // fighting autoPlay (reseeks to trimStart, stalls/loops). Rebase the clip to
    // frame 0 of the destination timeline; trim is preserved.
    if (
      isMediaPluginId(pluginName) &&
      data.box.parent !== ids.parent &&
      transformedBox.newBox.timeline &&
      typeof transformedBox.newBox.timeline.startFrame === "number"
    ) {
      const tl = transformedBox.newBox.timeline;
      const length = typeof tl.endFrame === "number" ? tl.endFrame - tl.startFrame : tl.duration;
      transformedBox.newBox.timeline = {
        ...tl,
        startFrame: 0,
        endFrame: typeof length === "number" ? length : tl.endFrame,
        duration: typeof length === "number" ? length : tl.duration,
      };
    }
    let transformedChildren = {};
    let marks = data.marks;
    let newMarks = {};
    if (data.childBoxes && data.childToolbars) {
      for (let bid in transformedBox.newIds) {
        let idsChild = {
          id: transformedBox.newIds[bid],
          parent: ids.id,
          container: data.childBoxes[bid].container,
        };
        let transformedBoxChild = this.transformBox(data.childBoxes[bid], idsChild, false, false);
        let transformedToolbarChild = this.transformToolbar(
          data.childToolbars[bid],
          idsChild,
          false,
          false
        );
        transformedChildren[transformedBox.newIds[bid]] = {
          box: transformedBoxChild.newBox,
          toolbar: transformedToolbarChild,
        };
      }
    }
    for (let mark in marks) {
      let newId = marks[mark].id + Date.now() + "_1";
      let newMark = {
        ...marks[mark],
        origin: transformedBox.newIds[marks[mark].origin] || ids.id,
        id: newId,
      };
      if (
        (isContainedView(newMark.connection) && this.props.containedViews[newMark.connection]) ||
        (isView(newMark.connection) && this.props.navItems[newMark.connection]) ||
        newMark.connectMode === "external" ||
        newMark.connectMode === "popup"
      ) {
        newMarks[newId] = newMark;
      }
    }

    this.props.onBoxPasted(
      { ...ids, config },
      transformedBox.newBox,
      transformedToolbar,
      transformedChildren,
      index,
      newMarks,
      data.score
    );
  }

  /**
   * Calculates if the current focused element in the DOM is a text area. If it is we do not want to paste the box.
   */
  /**
   * Returns true when there is a non-empty (non-collapsed) text selection in the
   * document. Used to defer to the browser's native copy of highlighted text.
   */
  hasTextSelection() {
    try {
      const sel = window.getSelection && window.getSelection();
      return !!sel && !sel.isCollapsed && sel.toString().trim().length > 0;
    } catch (e) {
      return false;
    }
  }

  containsCKEDitorText(activeElement) {
    if (!activeElement || !activeElement.classList) {
      return false;
    }
    let focus = activeElement.classList;
    return (
      focus.contains("form-control") ||
      focus.contains("cke_editable") ||
      focus.contains("textAreaStyle") ||
      // Inline text editing (InlineHtmlEditor) is a plain contentEditable, not
      // CKEditor, so the class checks above never matched it and copy/cut/paste
      // fell through to the canvas handlers — pasting created a new box instead
      // of inserting text. isContentEditable also covers nested nodes.
      activeElement.isContentEditable === true ||
      activeElement.tagName === "TEXTAREA" ||
      activeElement.tagName === "INPUT"
    );
  }

  /**
   * Paste action listener
   */
  pasteListener(event, overrideShiftKey) {
    // The Ctrl+Shift+V keydown path passes a KeyboardEvent (no clipboardData);
    // the browser's own paste event that follows does the real work.
    if (!event.clipboardData) return;
    let clipboard = event.clipboardData.getData("text");
    let page = this.currentPage();
    let boxIds = [];
    if (event.shiftKey && !overrideShiftKey) {
      return;
    }
    let activeElement = document.activeElement;

    if (clipboard.length > 1) {
      try {
        let parsed = JSON.parse(clipboard);
        if (typeof parsed === "object" && parsed !== null) {
          // Scene payload — handle before element checks
          if (parsed.__aideaType === "scene") {
            event.preventDefault();
            this.beginScenePaste(parsed);
            return;
          }
          clipboard = parsed;
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (Array.isArray(clipboard) && clipboard.length > 1 && this.props.boxSelected) {
      for (let index = 0; index < clipboard.length; index++) {
        let a = clipboard[index].box.id;
        boxIds.push(a);
      }
      if (event.clipboardData) {
        // Check if copied data is plugin
        let dataArray = "";
        try {
          let clipboardData = event.clipboardData.getData("text");
          dataArray = JSON.parse(clipboardData);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err, event);
        }
        boxIds.map((box) => {
          if (page) {
            let containerId = ID_PREFIX_SORTABLE_CONTAINER + Date.now();
            let id = ID_PREFIX_BOX + Date.now();
            let isTargetSlide = isSlide(page.type);
            let parent = isTargetSlide ? page.id : page.boxes[0];
            let row = 0;
            let col = 0;
            let container = isTargetSlide ? 0 : containerId;
            let newInd;
            if (
              this.props.boxSelected &&
              this.props.boxes[this.props.boxSelected] &&
              isBox(this.props.boxSelected)
            ) {
              parent = this.props.boxes[this.props.boxSelected].parent;
              container = this.props.boxes[this.props.boxSelected].container;
              // isTargetSlide = container === 0;
              row = this.props.boxes[this.props.boxSelected].row;
              col = this.props.boxes[this.props.boxSelected].col;
              newInd = this.getIndex(box, container);
            }
            let ids = { id, parent, container, row, col, page: page ? page.id : 0 };
            let data = dataArray.find((r) => r.box && r.box.id == box);

            // Copied data is an EditorBox
            if (data && data.box && data.toolbar) {
              // Focus is outside a text box
              if (!this.containsCKEDitorText(activeElement)) {
                // Paste plugin
                event.preventDefault();
                event.stopPropagation();
                // TODO Drag with Ctrl key held
                /*  data.map((data) => {
                                     data = {...data, [box.id] : box}
                                }) */
                this.pasteBox(data, ids, isTargetSlide, newInd);
                // Scroll into pasted element
                let boxCreated = findBox(ids.id);
                scrollElement(boxCreated);
              } else {
                // Inside a text box (CKEditor or input)
                // Let normal paste work
                // event.preventDefault();
              }
              // Copied data is not an EditorBox
            } else if (!this.containsCKEDitorText(activeElement)) {
              event.preventDefault();
              let imageBlob;
              let initialParams = {
                id: ID_PREFIX_BOX + Date.now(),
                parent: parent, //
                container: container,
                row: row,
                col: col,
                index: newInd,
                page: page ? page.id : 0,
                position:
                  isTargetSlide && container === 0
                    ? {
                        type: "absolute",
                        x: randomPositionGenerator(20, 40),
                        y: randomPositionGenerator(20, 30),
                      }
                    : { type: "relative", x: "0%", y: "0%" },
              };
              // If it is an image
              let noImage = true;
              try {
                let uploadFunction = this.props.uploadFunction;
                noImage = retrieveImageFromClipboardAsBase64(
                  event,
                  uploadFunction,
                  (url) => {
                    if (url) {
                      initialParams.url = url; // URLObj.createObjectURL(imageBlob);
                      createBox(
                        initialParams,
                        "HotspotImages",
                        isTargetSlide,
                        this.props.onBoxAdded,
                        this.props.boxes
                      );
                      return;
                    }
                    if (url) {
                      initialParams.url = url; // URLObj.createObjectURL(imageBlob);
                      createBox(
                        initialParams,
                        "Animations",
                        isTargetSlide,
                        this.props.onBoxAdded,
                        this.props.boxes
                      );
                      return;
                    }
                  },
                  false
                );
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log(err);
              }
              if (noImage) {
                let text =
                  event.clipboardData.getData("text/html") ||
                  event.clipboardData.getData("text/plain");
                try {
                  let el = $(text);
                  let tag = el.prop("tagName");
                  let type = el.attr("objecttype");
                  let src = el.attr("src");
                  if (tag === "IFRAME") {
                    if (type === "scormpackage") {
                      initialParams.url = src;
                      createBox(
                        initialParams,
                        "ScormPackage",
                        isTargetSlide,
                        this.props.onBoxAdded,
                        this.props.boxes
                      );
                      return;
                    }
                    // check if it is a youtube iframe
                    if (isURL(src) && src.includes("youtube")) {
                      initialParams.url = src;
                      createBox(
                        initialParams,
                        "EnrichedPlayer",
                        isTargetSlide,
                        this.props.onBoxAdded,
                        this.props.boxes
                      );
                      return;
                    }
                    initialParams.url = src;
                    createBox(
                      initialParams,
                      "Webpage",
                      isTargetSlide,
                      this.props.onBoxAdded,
                      this.props.boxes
                    );
                    return;
                  } else if (tag === "EMBED") {
                    initialParams.url = src;
                    createBox(
                      initialParams,
                      "FlashObject",
                      isTargetSlide,
                      this.props.onBoxAdded,
                      this.props.boxes
                    );
                    return;
                  } else if (tag === "AUDIO") {
                    if (!src) {
                      let source = el.find("source");
                      if (source && source[0]) {
                        src = $(source[0]).attr("src");
                      }
                    }
                    initialParams.url = src;
                    createBox(
                      initialParams,
                      "EnrichedAudio",
                      isTargetSlide,
                      this.props.onBoxAdded,
                      this.props.boxes
                    );
                    return;
                  } else if (tag === "VIDEO") {
                    if (!src) {
                      let source = el.find("source");
                      if (source && source[0]) {
                        src = $(source[0]).attr("src");
                      }
                    }
                    initialParams.url = src;
                    createBox(
                      initialParams,
                      "EnrichedPlayer",
                      isTargetSlide,
                      this.props.onBoxAdded,
                      this.props.boxes
                    );
                    return;
                  }
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.log(err);
                }
                initialParams.text = text;
                if (isURL(initialParams.text)) {
                  initialParams.text =
                    '<a href="' + initialParams.text + '">' + initialParams.text + "</a>";
                }
                createBox(
                  initialParams,
                  "BasicText",
                  isTargetSlide,
                  this.props.onBoxAdded,
                  this.props.boxes
                );
              }
            }
          }
        });
      }
    } else if (event.clipboardData) {
      // Check if copied data is plugin
      let data = "";
      try {
        let clipboardData = event.clipboardData.getData("text");
        data = JSON.parse(clipboardData);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err, event);
      }
      let page = this.currentPage();
      if (page) {
        let containerId = ID_PREFIX_SORTABLE_CONTAINER + Date.now();
        let id = ID_PREFIX_BOX + Date.now();
        let isTargetSlide = isSlide(page.type);
        let parent = isTargetSlide ? page.id : page.boxes[0];
        let row = 0;
        let col = 0;
        let container = isTargetSlide ? 0 : containerId;
        let newInd;
        if (
          this.props.boxSelected &&
          this.props.boxes[this.props.boxSelected] &&
          isBox(this.props.boxSelected)
        ) {
          parent = this.props.boxes[this.props.boxSelected].parent;
          container = this.props.boxes[this.props.boxSelected].container;
          // isTargetSlide = container === 0;
          row = this.props.boxes[this.props.boxSelected].row;
          col = this.props.boxes[this.props.boxSelected].col;
          newInd = this.getIndex(parent, container);
        }
        let ids = { id, parent, container, row, col, page: page ? page.id : 0 };
        // let data = dataArray.find(r => r.box && r.box.id == box);
        // Copied data is an EditorBox
        if (data && data.box && data.toolbar) {
          // Focus is outside a text box
          if (!this.containsCKEDitorText(activeElement)) {
            // Paste plugin
            event.preventDefault();
            event.stopPropagation();
            // TODO Drag with Ctrl key held
            /*  data.map((data) => {
                                 data = {...data, [box.id] : box}
                            }) */
            this.pasteBox(data, ids, isTargetSlide, newInd);
            // Scroll into pasted element
            let boxCreated = findBox(ids.id);
            scrollElement(boxCreated);
          } else {
            // Inside a text box (CKEditor or input)
            // Let normal paste work
            // event.preventDefault();
          }
          // Copied data is not an EditorBox
        } else if (!this.containsCKEDitorText(activeElement)) {
          event.preventDefault();
          let imageBlob;
          let initialParams = {
            id: ID_PREFIX_BOX + Date.now(),
            parent: parent, //
            container: container,
            row: row,
            col: col,
            index: newInd,
            page: page ? page.id : 0,
            position:
              isTargetSlide && container === 0
                ? {
                    type: "absolute",
                    x: randomPositionGenerator(20, 40),
                    y: randomPositionGenerator(20, 30),
                  }
                : { type: "relative", x: "0%", y: "0%" },
          };
          // If it is an image
          let noImage = true;
          try {
            let uploadFunction = this.props.uploadFunction;
            noImage = retrieveImageFromClipboardAsBase64(
              event,
              uploadFunction,
              (url) => {
                if (url) {
                  initialParams.url = url; // URLObj.createObjectURL(imageBlob);
                  createBox(
                    initialParams,
                    "HotspotImages",
                    isTargetSlide,
                    this.props.onBoxAdded,
                    this.props.boxes
                  );
                  return;
                }
                if (url) {
                  initialParams.url = url; // URLObj.createObjectURL(imageBlob);
                  createBox(
                    initialParams,
                    "Animations",
                    isTargetSlide,
                    this.props.onBoxAdded,
                    this.props.boxes
                  );
                  return;
                }
              },
              false
            );
          } catch (err) {
            // eslint-disable-next-line no-console
            console.log(err);
          }
          if (noImage) {
            let text =
              event.clipboardData.getData("text/html") || event.clipboardData.getData("text/plain");
            try {
              let el = $(text);
              let tag = el.prop("tagName");
              let type = el.attr("objecttype");
              let src = el.attr("src");
              if (tag === "IFRAME") {
                if (type === "scormpackage") {
                  initialParams.url = src;
                  createBox(
                    initialParams,
                    "ScormPackage",
                    isTargetSlide,
                    this.props.onBoxAdded,
                    this.props.boxes
                  );
                  return;
                }
                // check if it is a youtube iframe
                if (isURL(src) && src.includes("youtube")) {
                  initialParams.url = src;
                  createBox(
                    initialParams,
                    "EnrichedPlayer",
                    isTargetSlide,
                    this.props.onBoxAdded,
                    this.props.boxes
                  );
                  return;
                }
                initialParams.url = src;
                createBox(
                  initialParams,
                  "Webpage",
                  isTargetSlide,
                  this.props.onBoxAdded,
                  this.props.boxes
                );
                return;
              } else if (tag === "EMBED") {
                initialParams.url = src;
                createBox(
                  initialParams,
                  "FlashObject",
                  isTargetSlide,
                  this.props.onBoxAdded,
                  this.props.boxes
                );
                return;
              } else if (tag === "AUDIO") {
                if (!src) {
                  let source = el.find("source");
                  if (source && source[0]) {
                    src = $(source[0]).attr("src");
                  }
                }
                initialParams.url = src;
                createBox(
                  initialParams,
                  "EnrichedAudio",
                  isTargetSlide,
                  this.props.onBoxAdded,
                  this.props.boxes
                );
                return;
              } else if (tag === "VIDEO") {
                if (!src) {
                  let source = el.find("source");
                  if (source && source[0]) {
                    src = $(source[0]).attr("src");
                  }
                }
                initialParams.url = src;
                createBox(
                  initialParams,
                  "EnrichedPlayer",
                  isTargetSlide,
                  this.props.onBoxAdded,
                  this.props.boxes
                );
                return;
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.log(err);
            }
            initialParams.text = text;
            if (isURL(initialParams.text)) {
              initialParams.text =
                '<a href="' + initialParams.text + '">' + initialParams.text + "</a>";
            }
            createBox(
              initialParams,
              "BasicText",
              isTargetSlide,
              this.props.onBoxAdded,
              this.props.boxes
            );
          }
        }
      }
    }
  }

  /**
   * Modifies pasted box so it adapts to its new parent
   */
  transformBox(box, ids, isTargetSlide, isOriginSlide) {
    let samePage = isTargetSlide && box.parent === ids.parent;
    let newIds = {};
    let newContainerBoxes = {};
    let ind = 0;
    if (box.sortableContainers) {
      newContainerBoxes = JSON.parse(JSON.stringify(box.sortableContainers));
      for (let sc in newContainerBoxes) {
        for (let b in newContainerBoxes[sc].children) {
          let newID = ID_PREFIX_BOX + Date.now() + "_" + ind++;
          newIds[newContainerBoxes[sc].children[b]] = newID;
          newContainerBoxes[sc].children[b] = newID;
        }
      }
    }
    let newBox = Object.assign({}, box, {
      container: ids.container,
      id: ids.id,
      parent: ids.parent,
      position: isTargetSlide
        ? {
            type: "absolute",
            x: !samePage ? box.position.x : randomPositionGenerator(20, 40),
            y: !samePage ? box.position.y : randomPositionGenerator(20, 30),
          }
        : { type: "relative", x: "0%", y: "0%" },
      resizable: isTargetSlide,
      row: ids.row || 0,
      col: ids.col || 0,
      level: isBox(ids.parent) ? 1 : 0,
      sortableContainers: newContainerBoxes,
      containedViews: box.containedViews.filter((cv) => this.props.containedViews[cv]),
    });
    return { newBox, newIds };
  }

  /**
   * Modifies pasted toolbar so it adapts to its new parent
   */
  transformToolbar(toolbar, ids, isTargetSlide, isOriginSlide) {
    let newToolbar = Object.assign({}, toolbar, { id: ids.id });
    if (isTargetSlide !== isOriginSlide) {
      let config = IdealStudio.Plugins.get(newToolbar.pluginId).getConfig();
      if (isTargetSlide) {
        // TODO width VS bwidth?
        newToolbar.structure.width =
          parseFloat(config.initialWidthSlide || config.initialWidth) || "25";
        newToolbar.structure.height =
          parseFloat(config.initialHeightSlide || config.initialHeight) || "auto";
        newToolbar.structure.widthUnit = "%";
        newToolbar.structure.heightUnit = "%";
      } else {
        newToolbar.structure.width = parseFloat(config.initialWidth) || "25";
        newToolbar.structure.widthUnit = config.initialWidth.indexOf("px") !== -1 ? "px" : "%";
        newToolbar.structure.height = parseFloat(config.initialHeight) || "auto";
        newToolbar.structure.heightUnit = config.initialHeight.indexOf("px") !== -1 ? "px" : "%";
      }
    }
    if (isTargetSlide) {
      newToolbar.structure.widthUnit = "%";
      newToolbar.structure.heightUnit = "%";
    }
    return newToolbar;
  }

  getIndex(parent, container) {
    let newInd;
    if (isSortableContainer(container)) {
      let children = this.props.boxes[parent].sortableContainers[container].children;
      newInd = children.indexOf(this.props.boxSelected) + 1;
      newInd =
        newInd === 0 ? 1 : newInd === -1 || newInd >= children.length ? children.length : newInd;
    }
    return newInd;
  }

  /**
   * After component mounts
   * Sets listeners
   */
  componentDidMount() {
    document.addEventListener("copy", this.copyListener);
    document.addEventListener("paste", this.pasteListener);
    document.addEventListener("cut", this.cutListener);
    document.addEventListener("keyup", this.duplicateListener);
  }

  /**
   * Before component unmounts
   * Unsets listeners
   */
  componentWillUnmount() {
    document.removeEventListener("copy", this.copyListener);
    document.removeEventListener("paste", this.pasteListener);
    document.removeEventListener("cut", this.cutListener);
    document.removeEventListener("keyup", this.duplicateListener);
  }

  /**
   * Renders React Component
   */
  render() {
    let childrenWithProps = React.Children.map(this.props.children, (child) => {
      if (React.isValidElement(child) && child.props.name === "duplicate") {
        return React.cloneElement(child, { onClick: this.duplicateBox });
      }
      if (React.isValidElement(child) && child.props.name === "copy") {
        return React.cloneElement(child, { onClick: this.copyButtonListener });
      }
      if (React.isValidElement(child) && child.props.name === "copyScene") {
        return React.cloneElement(child, { onClick: this.copyScene });
      }
      return child;
    });

    const { scenePasteModal } = this.state;
    const scenePasteModalEl = scenePasteModal ? (
      <ScenePasteModal
        key="scene-paste-modal"
        show
        counts={scenePasteModal.payload.assetCounts}
        crossTraining={scenePasteModal.crossTraining}
        busy={scenePasteModal.busy}
        onCancel={() => this.setState({ scenePasteModal: null })}
        onConfirm={this.confirmScenePaste}
      />
    ) : null;

    return [this.state.alert, scenePasteModalEl, ...(childrenWithProps || [])];
  }
}
Clipboard.propTypes = {
  /**
   * Paste box function
   */
  onBoxPasted: PropTypes.func.isRequired,
  /**
   * Delete box function
   */
  onBoxDeleted: PropTypes.func.isRequired,
  /**
   * Selected box
   */
  boxSelected: PropTypes.any,
  /**
   * Object that contains the toolbars
   */
  toolbars: PropTypes.object,
  /**
   * Object containing all created boxes (by id)
   */
  boxes: PropTypes.object,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any,
  /**
   * Contained view selected
   */
  containedViewSelected: PropTypes.any,
  /**
   * Object that contains all the views
   */
  navItems: PropTypes.object,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.any,
  /**
   * Children components
   */
  children: PropTypes.any,
  /**
   * Object containing all marks
   */
  marks: PropTypes.object,
  /**
   * Function for adding a new box
   */
  onBoxAdded: PropTypes.func.isRequired,
  /**
   * Object containing all exercises
   */
  exercises: PropTypes.object,
  /**
   *  Function for uploading a file to the server
   */
  uploadFunction: PropTypes.func.isRequired,
  /** viewToolbarsById map (for scene copy) */
  viewToolbars: PropTypes.object,
  /** animationsById map (for scene copy) */
  animations: PropTypes.object,
  /** soundList map keyed by sceneId (for scene copy) */
  soundList: PropTypes.object,
  /** Called with rekeyed scene after paste confirmed */
  onScenePasted: PropTypes.func,
  /** Triggers the editor save */
  onSave: PropTypes.func,
  /** Selects a nav item by id after scene inserted */
  onNavItemSelected: PropTypes.func,
  globalConfig: PropTypes.object,
};
